import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getExperiment,
  updateExperiment,
  addExperimentVersion,
  updateExperimentVersion,
  removeExperimentVersion,
  getDetailedExperimentAnalytics,
  getVersionAnalytics,
  getPrompt,
  getCurrentUser,
  getBranches
} from '../services/api';
import AdvancedExperimentAnalytics from '../components/AdvancedExperimentAnalytics';
import PromptDiffViewer from '../components/PromptDiffViewer';
import { useFeedback } from '../contexts/FeedbackContext';
import { 
  AlertCircle, 
  Play, 
  Pause, 
  Settings, 
  Plus, 
  TrendingUp, 
  Users, 
  Target,
  GitCompare,
  BarChart3,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  GitBranch,
  ChevronDown,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';

const ExperimentDetail = () => {
  const { experimentId } = useParams();
  const navigate = useNavigate();
  const { showContextualFeedback } = useFeedback();
  const [experiment, setExperiment] = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [promptVersions, setPromptVersions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState(30);
  const [drillDownData, setDrillDownData] = useState(null);
  const [selectedComparisonVersions, setSelectedComparisonVersions] = useState([]);
  const [newVersion, setNewVersion] = useState({
    promptVersionId: '',
    versionName: '',
    trafficPercentage: 0,
    isControl: false
  });
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [copiedExperimentId, setCopiedExperimentId] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const userResponse = await getCurrentUser();
      setUser(userResponse);
      
      const [experimentResponse, detailedAnalyticsResponse] = await Promise.all([
        getExperiment(experimentId, userResponse.id),
        getDetailedExperimentAnalytics(experimentId, userResponse.id, analyticsTimeRange)
      ]);
      
      setExperiment(experimentResponse);
      setDetailedAnalytics(detailedAnalyticsResponse);

      // Load prompt versions and branches for the version selector
      if (experimentResponse?.prompt_id) {
        const [promptResponse, branchesResponse] = await Promise.all([
          getPrompt(experimentResponse.prompt_id),
          getBranches(experimentResponse.prompt_id, userResponse.id)
        ]);
        
        if (promptResponse?.versions) {
          setPromptVersions(promptResponse.versions);
        }
        
        if (branchesResponse) {
          setBranches(branchesResponse);
          // Set default branch as initially selected
          const defaultBranch = branchesResponse.find(b => b.is_default);
          if (defaultBranch) {
            setSelectedBranch(defaultBranch);
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [experimentId, analyticsTimeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTimeRangeChange = async (days) => {
    setAnalyticsTimeRange(days);
    try {
      const detailedAnalyticsResponse = await getDetailedExperimentAnalytics(experimentId, user.id, days);
      setDetailedAnalytics(detailedAnalyticsResponse);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleDrillDown = async (version) => {
    if (!version) {
      setDrillDownData(null);
      return;
    }

    try {
      const versionAnalytics = await getVersionAnalytics(
        experimentId, 
        version.version_id, 
        user.id, 
        7
      );
      setDrillDownData({
        version,
        analytics: versionAnalytics
      });
    } catch (error) {
      console.error('Error loading version analytics:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateExperiment(experimentId, { status: newStatus, userId: user.id });
      loadData();

      // Show contextual feedback after changing experiment status
      showContextualFeedback({
        actionName: 'Change Experiment Status',
        actionContext: `Changed experiment "${experiment?.name}" status to ${newStatus}`,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Error updating experiment status:', error);
      alert('Failed to update experiment status');
    }
  };

  const handleAddVersion = async (e) => {
    e.preventDefault();
    try {
      await addExperimentVersion(experimentId, {
        ...newVersion,
        userId: user.id
      });
      setShowAddVersionModal(false);
      setNewVersion({
        promptVersionId: '',
        versionName: '',
        trafficPercentage: 0,
        isControl: false
      });
      loadData();

      // Show contextual feedback after adding version to experiment
      showContextualFeedback({
        actionName: 'Add Version to Experiment',
        actionContext: `Added version "${newVersion.versionName}" to experiment "${experiment?.name}"`,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Error adding version:', error);
      alert('Failed to add version to experiment');
    }
  };

  const handleUpdateVersionTraffic = async (versionId, newTraffic) => {
    try {
      await updateExperimentVersion(experimentId, versionId, {
        trafficPercentage: newTraffic,
        userId: user.id
      });
      loadData();
    } catch (error) {
      console.error('Error updating version traffic:', error);
      alert('Failed to update version traffic');
    }
  };

  const handleRemoveVersion = async (versionId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Version',
      message: 'Are you sure you want to remove this version from the experiment?',
      onConfirm: async () => {
        try {
          await removeExperimentVersion(experimentId, versionId, user.id);
          loadData();
          setConfirmDialog({ isOpen: false });
        } catch (error) {
          console.error('Error removing version:', error);
          alert('Failed to remove version from experiment');
        }
      },
      onCancel: () => setConfirmDialog({ isOpen: false })
    });
  };

  const handleComparisonVersionSelect = (versionId) => {
    setSelectedComparisonVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 2) {
        return [...prev, versionId];
      } else {
        return [prev[1], versionId]; // Replace first with new selection
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-celadon-100 text-celadon-800 border-celadon-200';
      case 'paused': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'archived': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <Play className="h-4 w-4" />;
      case 'paused': return <Pause className="h-4 w-4" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4" />;
      case 'archived': return <XCircle className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const calculateTotalTraffic = () => {
    const total = experiment?.versions?.reduce((sum, version) => {
      const percentage = parseFloat(version.traffic_percentage) || 0;
      return sum + percentage;
    }, 0) || 0;
    return total.toFixed(1);
  };

  const getPerformanceData = () => {
    if (!detailedAnalytics?.overview) return null;
    
    const totalRequests = detailedAnalytics.overview.reduce((sum, v) => sum + (parseInt(v.request_count) || 0), 0);
    const avgScore = detailedAnalytics.overview.reduce((sum, v) => sum + (parseFloat(v.avg_auto_eval_score) || 0), 0) / detailedAnalytics.overview.length;
    const totalFailures = detailedAnalytics.overview.reduce((sum, v) => sum + (parseInt(v.failure_count) || 0), 0);
    const successRate = totalRequests > 0 ? ((totalRequests - totalFailures) / totalRequests) * 100 : 0;
    
    return { totalRequests, avgScore, successRate, totalFailures };
  };

  // Get filtered prompt versions for the selected branch
  const getFilteredPromptVersions = () => {
    if (!selectedBranch) return [];
    return promptVersions.filter(version => version.branch_id === selectedBranch.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-celadon-500"></div>
      </div>
    );
  }

  if (!experiment) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Experiment not found</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">The experiment you're looking for doesn't exist or you don't have access to it.</p>
      </div>
    );
  }

  const performanceData = getPerformanceData();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
              {/* Professional Header */}
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 rounded-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{experiment.name}</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{experiment.description}</p>
                  </div>
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(experiment.status)}`}>
                    {getStatusIcon(experiment.status)}
                    <span className="capitalize">{experiment.status}</span>
                  </div>
                </div>
                
                <div className="flex items-center mt-4 space-x-6 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Target className="h-4 w-4" />
                    <span>Sticky: {experiment.sticky_type}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>Created {new Date(experiment.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {experiment.status === 'draft' && (
                  <button
                    onClick={() => handleStatusChange('running')}
                    className="bg-celadon-600 text-white px-4 py-2 rounded-lg hover:bg-celadon-700 transition-colors flex items-center space-x-2"
                  >
                    <Play className="h-4 w-4" />
                    <span>Start Experiment</span>
                  </button>
                )}
                {experiment.status === 'running' && (
                  <button
                    onClick={() => handleStatusChange('paused')}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  >
                    <Pause className="h-4 w-4" />
                    <span>Pause</span>
                  </button>
                )}
                {experiment.status === 'paused' && (
                  <button
                    onClick={() => handleStatusChange('running')}
                    className="bg-celadon-600 text-white px-4 py-2 rounded-lg hover:bg-celadon-700 transition-colors flex items-center space-x-2"
                  >
                    <Play className="h-4 w-4" />
                    <span>Resume</span>
                  </button>
                )}
                <button
                  onClick={() => navigate(`/prompts/${experiment.prompt_id}`)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>View Prompt</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Performance Dashboard */}
        {performanceData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Requests</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{performanceData.totalRequests.toLocaleString()}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-celadon-500" />
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Success Rate</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{performanceData.successRate.toFixed(1)}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-celadon-600" />
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Score</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{performanceData.avgScore.toFixed(2)}</p>
                </div>
                <Target className="h-8 w-8 text-gray-500" />
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Versions</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{experiment.versions?.length || 0}</p>
                </div>
                <Users className="h-8 w-8 text-gray-600" />
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: Settings },
                { id: 'versions', label: 'Versions', icon: GitCompare },
                { id: 'analytics', label: 'Analytics', icon: BarChart3 }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-celadon-500 text-celadon-600'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Experiment Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Experiment Details</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400">Experiment ID:</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-900 dark:text-gray-100 font-mono text-xs">{experimentId}</span>
                          <button
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(experimentId);
                                setCopiedExperimentId(true);
                                setTimeout(() => setCopiedExperimentId(false), 2000);
                              } catch (err) {
                                // Fallback for older browsers
                                const tempInput = document.createElement('input');
                                tempInput.value = experimentId;
                                document.body.appendChild(tempInput);
                                tempInput.select();
                                document.execCommand('copy');
                                document.body.removeChild(tempInput);
                                setCopiedExperimentId(true);
                                setTimeout(() => setCopiedExperimentId(false), 2000);
                              }
                            }}
                            className="flex items-center justify-center p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                            title="Copy Experiment ID"
                          >
                            {copiedExperimentId ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Description:</span>
                        <span className="text-gray-900 dark:text-gray-100">{experiment.description || 'No description'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Sticky Type:</span>
                        <span className="text-gray-900 dark:text-gray-100">{experiment.sticky_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(experiment.status)}`}>
                          {experiment.status ? experiment.status.charAt(0).toUpperCase() + experiment.status.slice(1) : 'Loading...'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Traffic Allocated:</span>
                        <span className="text-gray-900 dark:text-gray-100">{calculateTotalTraffic()}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'versions' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Experiment Versions</h3>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setSelectedComparisonVersions([])}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={() => setShowAddVersionModal(true)}
                      className="bg-celadon-600 text-white px-4 py-2 rounded-lg hover:bg-celadon-700 transition-colors flex items-center space-x-2"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Version</span>
                    </button>
                  </div>
                </div>

                {/* Version Selection for Comparison */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Eye className="h-4 w-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-800">
                      Select versions to compare prompts ({selectedComparisonVersions.length}/2)
                    </span>
                  </div>
                  <p className="text-xs text-gray-700">
                    Click on version cards below to select them for side-by-side comparison
                  </p>
                </div>

                {/* Version Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {experiment.versions?.map((version) => {
                    const isSelected = selectedComparisonVersions.includes(version.id);
                    const versionData = promptVersions.find(pv => pv.id === version.prompt_version_id);
                    const performance = detailedAnalytics?.overview?.find(ov => ov.version_id === version.id);
                    
                    return (
                      <div 
                        key={version.id} 
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-celadon-500 bg-celadon-50 shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleComparisonVersionSelect(version.id)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-3">
                            <div>
                              <h4 className="font-semibold text-gray-900">{version.version_name}</h4>
                              <div className="flex items-center space-x-2 mt-1">
                                {version.is_control && (
                                  <span className="px-2 py-1 text-xs font-medium bg-celadon-100 text-celadon-700 rounded-full">
                                    Control
                                  </span>
                                )}
                                {isSelected && (
                                  <span className="px-2 py-1 text-xs font-medium bg-celadon-200 text-celadon-800 rounded-full">
                                    Selected
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={version.traffic_percentage}
                              onChange={(e) => handleUpdateVersionTraffic(version.id, parseFloat(e.target.value))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-celadon-500"
                              title="Traffic percentage"
                            />
                            <span className="text-xs text-gray-500">%</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveVersion(version.id);
                              }}
                              className="text-gray-600 hover:text-gray-800 text-sm p-1"
                              title="Remove version"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {performance && (
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Requests:</span>
                              <div className="font-medium">{performance.request_count}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Score:</span>
                              <div className="font-medium">{parseFloat(performance.avg_auto_eval_score || 0).toFixed(2)}</div>
                            </div>
                            <div>
                              <span className="text-gray-600">Success:</span>
                              <div className="font-medium">
                                {((1 - (parseInt(performance.failure_count) || 0) / Math.max(parseInt(performance.request_count) || 1, 1)) * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        )}

                        {versionData && (
                          <div className="mt-3 p-3 bg-gray-50 rounded text-xs">
                            <div className="font-mono text-gray-700 line-clamp-3">
                              {versionData.content.substring(0, 150)}...
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Prompt Comparison */}
                {selectedComparisonVersions.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Prompt Comparison</h4>
                    <PromptDiffViewer
                      versions={selectedComparisonVersions.map(versionId => {
                        const expVersion = experiment.versions?.find(ev => ev.id === versionId);
                        const promptVersion = promptVersions.find(pv => pv.id === expVersion?.prompt_version_id);
                        return {
                          ...promptVersion,
                          id: versionId, // Use experiment version ID for consistency
                          version_name: expVersion?.version_name || `Version ${versionId}`,
                          is_control: expVersion?.is_control || false
                        };
                      }).filter(v => v && v.content)}
                      selectedVersions={selectedComparisonVersions}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <AdvancedExperimentAnalytics 
                  analytics={detailedAnalytics}
                  experiment={experiment}
                  onTimeRangeChange={handleTimeRangeChange}
                  onDrillDown={handleDrillDown}
                />

                {/* Drill-down Data Display */}
                {drillDownData && (
                  <div className="bg-white border rounded-xl p-6 shadow-sm">
                    <h4 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                      <Target className="h-5 w-5 text-celadon-600" />
                      <span>Detailed Analysis: {drillDownData.version.version_name}</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                          <Users className="h-4 w-4" />
                          <span>Input Clusters</span>
                        </h5>
                        {drillDownData.analytics.inputClusters?.map((cluster, index) => (
                          <div key={index} className="text-sm text-gray-600 mb-2 p-2 bg-gray-50 rounded">
                            <div className="font-medium">{cluster.name}</div>
                            <div className="text-xs">{cluster.count} inputs • Avg: {cluster.avgScore.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                          <TrendingUp className="h-4 w-4" />
                          <span>Top Performing</span>
                        </h5>
                        {drillDownData.analytics.performanceBreakdown?.topPerforming?.slice(0, 3).map((item, index) => (
                          <div key={index} className="text-sm mb-2 p-2 bg-green-50 rounded">
                            <div className="font-medium text-green-700">Score: {item.score.toFixed(2)}</div>
                            <div className="text-xs text-gray-600 truncate">"{item.input}"</div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                          <BarChart3 className="h-4 w-4" />
                          <span>Summary</span>
                        </h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between p-2 bg-gray-50 rounded">
                            <span className="text-gray-600">Total Logs:</span>
                            <span className="font-medium">{drillDownData.analytics.summary.totalLogs}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-gray-50 rounded">
                            <span className="text-gray-600">Avg Score:</span>
                            <span className="font-medium">{drillDownData.analytics.summary.avgScore.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between p-2 bg-gray-50 rounded">
                            <span className="text-gray-600">Avg Latency:</span>
                            <span className="font-medium">{Math.round(drillDownData.analytics.summary.avgLatency)}ms</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Add Version Modal */}
      {showAddVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-6">
              <Plus className="h-6 w-6 text-celadon-600" />
              <h2 className="text-xl font-semibold">Add Version to Experiment</h2>
            </div>
            
            <form onSubmit={handleAddVersion} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch *
                </label>
                <div className="relative">
                  <select
                    value={selectedBranch?.id || ''}
                    onChange={(e) => {
                      const branch = branches.find(b => b.id === e.target.value);
                      setSelectedBranch(branch);
                      // Reset prompt version selection when branch changes
                      setNewVersion({ ...newVersion, promptVersionId: '' });
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent appearance-none pr-10"
                    required
                  >
                    <option value="">Select a branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.is_default ? '(default)' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select the branch to choose a prompt version from
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt Version *
                </label>
                <select
                  value={newVersion.promptVersionId}
                  onChange={(e) => setNewVersion({ ...newVersion, promptVersionId: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent"
                  required
                  disabled={!selectedBranch}
                >
                  <option value="">
                    {selectedBranch ? 'Select a prompt version' : 'Select a branch first'}
                  </option>
                  {getFilteredPromptVersions().map((version) => (
                    <option key={version.id} value={version.id}>
                      v{version.version_number} - {version.content.substring(0, 50)}...
                      {version.is_active ? ' (active)' : ''}
                    </option>
                  ))}
                </select>
                {selectedBranch && getFilteredPromptVersions().length === 0 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    No versions available in the "{selectedBranch.name}" branch
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version Name *
                </label>
                <input
                  type="text"
                  value={newVersion.versionName}
                  onChange={(e) => setNewVersion({ ...newVersion, versionName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent"
                  required
                  placeholder="e.g., Version A, Control, Variant 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Traffic Percentage *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={newVersion.trafficPercentage}
                    onChange={(e) => setNewVersion({ ...newVersion, trafficPercentage: parseFloat(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent pr-8"
                    required
                    placeholder="0.0"
                  />
                  <span className="absolute right-3 top-2 text-gray-500 text-sm">%</span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isControl"
                  checked={newVersion.isControl}
                  onChange={(e) => setNewVersion({ ...newVersion, isControl: e.target.checked })}
                  className="h-4 w-4 text-celadon-600 focus:ring-celadon-500 border-gray-300 rounded"
                />
                <label htmlFor="isControl" className="text-sm text-gray-700">
                  This is the control version
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowAddVersionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-celadon-600 text-white rounded-lg hover:bg-celadon-700 transition-colors"
                >
                  Add Version
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog?.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <AlertCircle className="h-6 w-6 text-orange-500" />
              <h2 className="text-xl font-semibold">{confirmDialog.title}</h2>
            </div>
            
            <p className="text-gray-600 mb-6">{confirmDialog.message}</p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={confirmDialog.onCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperimentDetail; 